import { useLayoutEffect, useRef, useState } from 'react';

const IMAGE_URL = /\.(png|jpe?g|gif|webp|svg|bmp)$/i;

// Only remote pages can be previewed: local blobs hold the zip itself and the
// default HTML5 thumbnail is an image.
export const isHtmlPreviewUrl = (url) => {
    const value = String(url ?? '').trim();
    if (!/^https?:\/\//i.test(value)) return false;

    return !IMAGE_URL.test(value.split(/[?#]/)[0]);
};

const useElementSize = (enabled) => {
    const ref = useRef(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    useLayoutEffect(() => {
        const element = ref.current;
        if (!enabled || !element) return undefined;

        const update = () => {
            const rect = element.getBoundingClientRect();
            setSize((current) =>
                current.width === rect.width && current.height === rect.height
                    ? current
                    : { width: rect.width, height: rect.height }
            );
        };

        update();
        if (typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [enabled]);

    return [ref, size];
};

// Renders an HTML creative (zip / dynamic URL) at its real size and scales it
// down so the whole creative fits the preview box (a small iframe would only
// show its corner). Pass boxWidth/boxHeight for a fixed box; otherwise the
// preview fills its parent and measures it. With fit={false} the creative is
// shown at 100% and the parent box scrolls.
const HtmlCreativePreview = ({
    url,
    title,
    width,
    height,
    boxWidth,
    boxHeight,
    fit = true,
}) => {
    const hasFixedBox = Number(boxWidth) > 0 && Number(boxHeight) > 0;
    const [containerRef, measuredBox] = useElementSize(
        fit && !hasFixedBox
    );
    const box = hasFixedBox
        ? { width: Number(boxWidth), height: Number(boxHeight) }
        : measuredBox;

    const renderWidth = Number(width);
    const renderHeight = Number(height);
    const canScale =
        fit &&
        renderWidth > 0 &&
        renderHeight > 0 &&
        box.width > 0 &&
        box.height > 0;
    const scale = canScale
        ? Math.min(box.width / renderWidth, box.height / renderHeight)
        : 1;

    const iframe = (
        <iframe
            src={url}
            title={title}
            // allow-same-origin: dynamic creatives (Next.js apps) read
            // localStorage / document.cookie on boot, which throws in an
            // opaque-origin sandbox and leaves them black. The creative keeps
            // its own origin, so it still cannot reach the DSP page.
            sandbox="allow-scripts allow-same-origin"
            loading="lazy"
            scrolling="no"
            className="pointer-events-none block flex-shrink-0 shrink-0 border-0 bg-white"
            style={
                !fit && renderWidth > 0 && renderHeight > 0
                    ? { width: renderWidth, height: renderHeight }
                    : canScale
                    ? {
                          width: renderWidth,
                          height: renderHeight,
                          transform: `scale(${scale})`,
                          transformOrigin: 'center',
                      }
                    : { width: '100%', height: '100%' }
            }
            data-testid="HtmlCreativePreview"
        />
    );

    if (hasFixedBox) return iframe;

    // Not centered with flex: an overflowing centered child cannot be
    // scrolled back to its left/top edge.
    if (!fit) {
        return <div className="h-full w-full overflow-auto">{iframe}</div>;
    }

    return (
        <div
            ref={containerRef}
            className="flex h-full w-full items-center justify-center overflow-hidden">
            {iframe}
        </div>
    );
};

export default HtmlCreativePreview;
